import { createTheme } from '@mui/material/styles';

// Tokens lifted from coach_dashboard_v6.html mockup.
const neutral = {
  0: '#FFFFFF',
  25: '#FAFBFC',
  50: '#F4F6FA',     // page/canvas background (--bg)
  75: '#EDF0F6',     // border-light
  100: '#E4E8F0',    // borders
  200: '#D5DAE5',
  300: '#BCC2D2',
  400: '#A0AAC0',    // tertiary text (--t3)
  500: '#7A8398',
  600: '#5A6480',    // secondary text (--t2)
  700: '#3D465C',
  800: '#0F1420',    // primary text (--text)
  900: '#070B14',
};

const semantic = {
  success: '#16A376',   // same as brand green
  successBg: '#E8F7F2',
  warning: '#F59D0D',
  warningBg: '#FFF8E8',
  warningText: '#7A4D00',
  danger: '#E03E3E',
  dangerBg: '#FFF0F0',
  dangerText: '#8B1A1A',
  info: '#3B7CF4',
  infoBg: '#EDF2FF',
  infoText: '#1A3FA0',
  beta: '#7C3AED',
  betaBg: '#F3F0FF',
};

const fontFamily = [
  'Heebo',
  'Assistant',
  '-apple-system',
  'BlinkMacSystemFont',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
].join(',');

const shadows = [
  'none',
  '0 1px 3px rgba(15,20,40,0.05), 0 4px 20px rgba(15,20,40,0.07)',
  '0 2px 6px rgba(15,20,40,0.06), 0 1px 2px rgba(15,20,40,0.04)',
  '0 6px 16px rgba(15,20,40,0.08), 0 2px 4px rgba(15,20,40,0.04)',
  '0 10px 36px rgba(15,20,40,0.14)',
];
while (shadows.length < 25) shadows.push(shadows[4]);

const theme = createTheme({
  direction: 'rtl',
  palette: {
    primary: {
      light: '#3FD3C2',
      main: '#01BBA7',          // brand --green
      dark: '#019385',          // brand --green-dark
      contrastText: '#FFFFFF',
      surface: '#E3F8F5',       // brand --green-light
      surfaceStrong: '#CBF1EB',
      text: '#016E62',          // brand --green-text
      error: '#F16063',
      fats: '#FF9800',
      carbs: '#F77450',
      protein: '#50C2F7',
      calories: '#07BBA6',
    },
    secondary: {
      light: '#E7E9EE',
      main: '#E7E9EE',
      dark: '#E7E9EE',
      contrastText: '#FFFFFF',
      error: '#F16063',
    },
    neutral,
    semantic,
    background: {
      default: neutral[50],       // page canvas
      paper: '#FFFFFF',
      sidebar: '#FFFFFF',         // mockup sidebar is white on a gray canvas
    },
    text: {
      primary: neutral[800],
      secondary: neutral[600],
      tertiary: neutral[400],
      disabled: neutral[300],
    },
    divider: neutral[100],
  },
  shape: {
    borderRadius: 10,                                  // mockup --rs
    radii: { xs: 6, sm: 8, md: 10, lg: 16, pill: 999 }, // --rxs / --rs / --r
  },
  shadows,
  customShadows: {
    topbar: '0 1px 0 #E4E8F0, 0 2px 12px rgba(15,20,40,0.04)',
    card: '0 1px 3px rgba(15,20,40,0.05), 0 4px 20px rgba(15,20,40,0.07)',
    cardHover: '0 10px 36px rgba(15,20,40,0.14)',
  },
  typography: {
    fontFamily,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    body1: { fontSize: 14, lineHeight: 1.5, letterSpacing: 0 },
    body2: { fontSize: 13, lineHeight: 1.45 },
    subtitle2: {
      fontSize: 9.5,
      lineHeight: 1.3,
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: neutral[400],
    },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    h6: { fontSize: 16, lineHeight: 1.4, fontWeight: 800, letterSpacing: '-0.3px' },
  },
  components: {
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, paddingLeft: 14, paddingRight: 14 },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: 10 } },
    },
    MuiInputBase: {
      defaultProps: { autoComplete: 'off' },
      styleOverrides: { root: { borderRadius: 10 } },
    },
    MuiTextField: {
      defaultProps: { autoComplete: 'off' },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          boxShadow: '0 1px 3px rgba(15,20,40,0.05), 0 4px 20px rgba(15,20,40,0.07)',
          border: `1px solid ${neutral[100]}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 6px',
          fontSize: 13,
        },
      },
    },
    MuiTooltip: {
      // On a touch device MUI waits 700ms of long-press before showing a tooltip and hides it
      // after 1500ms — long enough that most people never see one, on an admin full of icon-only
      // buttons whose ONLY visible label is the tooltip. Show it as soon as the press registers
      // and leave it up long enough to read.
      defaultProps: {
        enterTouchDelay: 0,
        leaveTouchDelay: 3000,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: neutral[800],
          borderRadius: 6,
          fontSize: 11,
        },
      },
    },
  },
});

// Height of the environment banner (stage/local). Zero on production so the
// banner takes no space and the layout is unchanged in prod.
export const envBannerHeight =
  process.env.REACT_APP_ENV && process.env.REACT_APP_ENV !== "production" ? 28 : 0;

export const layout = {
  topBarHeight: 58,
  envBannerHeight,
  sideNavWidth: 190,
  // Symmetric horizontal gutter applied at the layout level so every page
  // has equal breathing room on left and right without needing per-page
  // padding. Matches the "16px 20px" Dashboard was using internally.
  gutter: 28,
  // Below `md` the same gutter is 10% of a 375px phone taken off BOTH sides — 56px of a
  // screen that the nutrition builder's food rows and the clients table are already fighting
  // for. Breathing room is a luxury of a wide window; on a phone the content needs the pixels.
  gutterMobile: 12,
  zIndex: {
    topBar: 1200,
    sideNav: 1100,
    sideNavMobile: 1300,
  },
};

export default theme;
